/**
 * Running inside the Android app (apps/android)? It loads this web app from the server and
 * marks its user agent, so features the app handles natively can step aside.
 *
 * Version 1.1+ of the app also offers `CommsNative`, a message channel (only for this
 * server's pages) for phone-call audio: earpiece / speaker / headset / Bluetooth, the screen
 * turning off at the ear, and an ongoing-call notification that keeps calls going in the
 * background. See MainActivity.java for the other end.
 */
import { create } from 'zustand';

export const isAndroidApp = (userAgent = navigator.userAgent) =>
  /\bCommsAndroid\/\d/.test(userAgent);

/** The Android app's build number (its user agent says `CommsAndroid/<n>`), or 0. */
export const androidAppVersion = (userAgent = navigator.userAgent) =>
  Number(/\bCommsAndroid\/(\d+)/.exec(userAgent)?.[1] ?? 0);

/** Phone notifications arrived in app 1.2 (build 3). */
export const androidAppHasPush = (userAgent = navigator.userAgent) =>
  androidAppVersion(userAgent) >= 3;

/** One-tap updates (download, check, open the installer) arrived in app 1.3 (build 4). */
export const androidAppInstallsUpdates = (userAgent = navigator.userAgent) =>
  androidAppVersion(userAgent) >= 4;

/** How an in-app update is going, as the app reports it. */
let inBackground = false;
/**
 * The Android app is open in the background. Its notifications come from the phone then
 * (pushed by the server), so the page doesn't also ring or chime.
 */
export const appInBackground = () => inBackground;
export const setAppInBackground = (background: boolean) => {
  inBackground = background;
};

export type UpdateState = 'downloading' | 'installing' | 'needs-permission' | 'failed';

export type AudioRoute = 'earpiece' | 'speaker' | 'headset' | 'bluetooth';

interface NativeChannel {
  postMessage(data: string): void;
  addEventListener(type: 'message', listener: (e: { data: string }) => void): void;
}

/** The public Firebase ids the app needs for this server's pushes (from GET /push/native). */
export interface FcmAndroidConfig {
  projectId: string;
  appId: string;
  apiKey: string;
  senderId: string;
}

export interface NativePushToken {
  token: string;
  keys: { p256dh: string; auth: string };
}

export interface NativePushState {
  /** Android's notification permission for the app */
  permission: 'granted' | 'denied';
  /** can incoming calls ring full-screen over the lock screen */
  fullScreen: boolean;
}

type ToNative =
  | { type: 'hello' }
  | { type: 'push-enable'; fcm: FcmAndroidConfig }
  | { type: 'push-disable' }
  | { type: 'push-status' }
  | { type: 'open-settings'; what: 'notifications' | 'full-screen' }
  | { type: 'install-update'; sha256: string }
  | { type: 'call-start'; video: boolean; title: string }
  | { type: 'call-update'; video: boolean; title: string }
  | { type: 'call-end' }
  | { type: 'route'; route: AudioRoute }
  | { type: 'reapply' };

type FromNative =
  | { type: 'routes'; available: AudioRoute[]; current: AudioRoute }
  | { type: 'playback'; voice: boolean }
  | { type: 'hangup' }
  | { type: 'app-state'; foreground: boolean }
  | { type: 'accept-call'; channelId: string }
  | { type: 'open'; url: string }
  | ({ type: 'push-token' } & NativePushToken)
  | { type: 'push-error'; message: string }
  | { type: 'update-state'; state: UpdateState; message?: string }
  | ({ type: 'push-state' } & NativePushState);

/** What the app tells the page (besides call audio), for lib/nativeApp.ts. */
export interface NativeEvents {
  appState?: (foreground: boolean) => void;
  acceptCall?: (channelId: string) => void;
  open?: (url: string) => void;
  updateState?: (state: UpdateState, message?: string) => void;
  /** app 1.4+, in a call: is the call playing as phone-call audio (false: media, loudspeaker only) */
  playback?: (voice: boolean) => void;
}

/**
 * The phone's audio outputs during a call, as the app reports them. `mediaOnly`: the call
 * still plays as media audio after trying again, so it can only use the loudspeaker.
 */
export const useNativeAudio = create<{
  available: AudioRoute[];
  current: AudioRoute | null;
  mediaOnly: boolean;
}>(() => ({ available: [], current: null, mediaOnly: false }));

const channel = () => (window as unknown as { CommsNative?: NativeChannel }).CommsNative;

let listeningTo: NativeChannel | null = null;
let hangup: (() => void) | null = null;
let events: NativeEvents = {};
type Waiter = { resolve: (m: FromNative) => void; types: FromNative['type'][] };
let waiters: Waiter[] = [];

function onMessage(e: { data: string }) {
  let m: FromNative;
  try {
    m = JSON.parse(e.data) as FromNative;
  } catch {
    return;
  }
  const waiting = waiters.filter((w) => w.types.includes(m.type));
  waiters = waiters.filter((w) => !w.types.includes(m.type));
  waiting.forEach((w) => w.resolve(m));
  if (m.type === 'routes') useNativeAudio.setState({ available: m.available, current: m.current });
  else if (m.type === 'hangup') hangup?.();
  else if (m.type === 'app-state') events.appState?.(m.foreground);
  else if (m.type === 'accept-call') events.acceptCall?.(m.channelId);
  else if (m.type === 'open') events.open?.(m.url);
  else if (m.type === 'update-state') events.updateState?.(m.state, m.message);
  else if (m.type === 'playback') events.playback?.(m.voice);
}

/** Send a message and wait for one of the given replies. */
function ask(m: ToNative, types: FromNative['type'][], timeoutMs: number): Promise<FromNative> {
  return new Promise((resolve, reject) => {
    const w: Waiter = { resolve, types };
    waiters.push(w);
    window.setTimeout(() => {
      if (!waiters.includes(w)) return;
      waiters = waiters.filter((x) => x !== w);
      reject(new Error('The app did not answer'));
    }, timeoutMs);
    post(m);
  });
}

function post(m: ToNative) {
  const c = channel();
  if (!c) return;
  if (listeningTo !== c) {
    c.addEventListener('message', onMessage);
    listeningTo = c;
  }
  c.postMessage(JSON.stringify(m));
}

export const nativeApp = {
  /** Say hello: the app can reach this page from now on (and replays anything pending). */
  hello(e: NativeEvents) {
    events = e;
    post({ type: 'hello' });
  },
  /** Turn on phone notifications: the app gets a Firebase token and its encryption keys. */
  async enablePush(fcm: FcmAndroidConfig): Promise<NativePushToken> {
    const r = await ask({ type: 'push-enable', fcm }, ['push-token', 'push-error'], 60_000);
    if (r.type === 'push-error') throw new Error(r.message);
    if (r.type !== 'push-token') throw new Error('Unexpected answer from the app');
    return { token: r.token, keys: r.keys };
  },
  disablePush: () => post({ type: 'push-disable' }),
  async pushState(): Promise<NativePushState> {
    const r = await ask({ type: 'push-status' }, ['push-state'], 10_000);
    if (r.type !== 'push-state') throw new Error('Unexpected answer from the app');
    return { permission: r.permission, fullScreen: r.fullScreen };
  },
  openSettings: (what: 'notifications' | 'full-screen') => post({ type: 'open-settings', what }),
  /** Download this server's newer app and open the installer (progress comes as updateState). */
  installUpdate: (sha256: string) => post({ type: 'install-update', sha256 }),
};

export const nativeCall = {
  /** The app can handle call audio (Android app 1.1 or newer). */
  available: () => !!channel(),
  /** What "Hang up" in the ongoing-call notification does. */
  onHangup(fn: () => void) {
    hangup = fn;
  },
  start: (video: boolean, title: string) => post({ type: 'call-start', video, title }),
  update: (video: boolean, title: string) => post({ type: 'call-update', video, title }),
  end() {
    post({ type: 'call-end' });
    useNativeAudio.setState({ available: [], current: null, mediaOnly: false });
  },
  route: (route: AudioRoute) => post({ type: 'route', route }),
  /** The microphone restarted: the app re-applies the chosen output. */
  reapply: () => post({ type: 'reapply' }),
};

/** The next output to switch to when the in-call button is tapped. */
export function nextRoute(available: AudioRoute[], current: AudioRoute | null): AudioRoute | null {
  if (available.length < 2) return null;
  const i = current ? available.indexOf(current) : -1;
  return available[(i + 1) % available.length]!;
}
